import { Component } from '@theme/component';
import {
  center,
  closest,
  clamp,
  getVisibleElements,
  mediaQueryLarge,
  prefersReducedMotion,
  preventDefault,
  viewTransition,
  scheduler,
} from '@theme/utilities';
import { Scroller, scrollIntoView } from '@theme/scrolling';
import { SlideshowSelectEvent } from '@theme/events';

// The threshold for determining visibility of slides.
const SLIDE_VISIBLITY_THRESHOLD = 0.7;

/**
 * Slideshow custom element that allows sliding between content.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} scroller
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLElement} [current]
 * @property {HTMLElement[]} [thumbnails]
 * @property {HTMLElement[]} [dots]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class Slideshow extends Component {
  static get observedAttributes() {
    return ['initial-slide'];
  }

  /**
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    // Collection page filtering will Morph slideshow galleries in place, updating
    // the slideshow[initial-slide] and slideshow-slide[hidden] attributes.
    // We need to re-select() the slide after the morph is complete, but not before
    // slideshow-slide elements have their [hidden] attribute updated.
    if (name === 'initial-slide' && oldValue !== newValue) {
      queueMicrotask(() => {
        // Only select if the component is connected and initialized
        if (!this.isConnected || !this.#scroll || !this.refs.slides) return;
        const index = parseInt(newValue, 10) || 0;
        const slide_id = this.refs.slides[index]?.getAttribute('slide-id');
        if (slide_id) {
          this.select({ id: slide_id }, undefined, { animate: false });
        }
      });
    }
  }

  requiredRefs = ['scroller'];

  async connectedCallback() {
    super.connectedCallback();

    // Wait for any in-progress view transitions to finish
    if (viewTransition.current) {
      await viewTransition.current;
      // It's possible that the slideshow was disconnected before the view transition finished
      if (!this.isConnected) return;
    }

    const slideCount = this.slides?.length || 0;
    slideCount <= 1 ? this.#setupSlideshowWithoutControls() : this.#setupSlideshow();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#scroll) {
      const { scroller } = this.refs;
      scroller.removeEventListener('mousedown', this.#handleMouseDown);
      scroller.removeEventListener('touchstart', this.#handleTouchStart);
      scroller.removeEventListener('touchend', this.#handleTouchEnd);
      scroller.removeEventListener('touchcancel', this.#handleTouchEnd);
      this.#scroll.destroy();
    }

    const slideCount = this.slides?.length || 0;
    if (slideCount > 1) {
      this.removeEventListener('mouseenter', this.suspend);
      this.removeEventListener('mouseleave', this.resume);
      this.removeEventListener('pointerenter', this.#handlePointerEnter);
      document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    }

    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
    }
  }

  /** Indicates whether the slideshow is nested inside another slideshow. */
  get isNested() {
    return this.parentElement?.closest('slideshow-component') !== null;
  }

  get initialSlide() {
    return this.refs.slides?.[this.initialSlideIndex];
  }

  /**
   * Selects a slide based on the input index.
   * @param {number|string|{id: string}} input - The index or id of the slide to select.
   * @param {Event} [event] - The event that triggered the selection.
   * @param {Object} [options] - The options for the selection.
   * @param {boolean} [options.animate=true] - Whether to animate the selection.
   */
  async select(input, event, options = {}) {
    if (this.#disabled || !this.refs.slides?.length) return;
    if (!this.#scroll) return;

    // Store the actual current slide before any mutations
    const currentSlide = this.slides?.[this.current];

    for (const slide of this.refs.slides) {
      if (slide.hasAttribute('reveal')) {
        slide.removeAttribute('reveal');
        slide.setAttribute('aria-hidden', 'true');
      }
    }

    // Figure out the raw desired index (could be -1 if user is on first slide and clicks prev)
    let requestedIndex = (() => {
      if (typeof input === 'number') return input;
      if (typeof input === 'string') return parseInt(input, 10);
      if ('id' in input) {
        const requestedSlide = this.refs.slides.find((slide) => slide.getAttribute('slide-id') == input.id);

        if (!requestedSlide || !this.slides) return;

        // Force the slide to be revealed if it is hidden
        if (requestedSlide.hasAttribute('hidden')) {
          requestedSlide.setAttribute('reveal', '');
          requestedSlide.setAttribute('aria-hidden', 'false');
        }

        return this.slides.indexOf(requestedSlide);
      }
    })();

    const { current } = this;
    const { slides } = this;

    // Guard checks: no slides, invalid index, or selecting the same slide
    if (!slides?.length || requestedIndex === undefined || isNaN(requestedIndex)) return;

    const requestedSlideElement = slides?.[requestedIndex];
    if (currentSlide === requestedSlideElement) return;

    if (!this.infinite) requestedIndex = clamp(requestedIndex, 0, slides.length - 1);

    event?.preventDefault();

    const { animate = true } = options;
    const lastIndex = slides.length - 1;

    // Decide the actual target index (clamp for infinite loop)
    let index = requestedIndex;
    if (requestedIndex < 0) index = lastIndex;
    else if (requestedIndex > lastIndex) index = 0;

    // TK: v nekonečném režimu rozhoduje VIZUÁLNÍ vzdálenost — snímek 0 stojí
    // hned vedle posledního, takže přetočení je běžný sousední posun.
    const isAdjacentSlide = this.#loopActive
      ? Math.abs(this.#visualPosition(index) - this.#visualPosition(current)) <= 1
      : Math.abs(index - current) <= 1 && requestedIndex >= 0 && requestedIndex <= lastIndex;
    const { visibleSlides } = this;
    // TK: placeholder trik níž přeskládává DOM, což by nekonečnému režimu
    // rozbilo `order`. Vzdálený skok (klik na náhled) proto jede instantně;
    // #recenterLoop() po dojezdu srovná okolí.
    const instant = prefersReducedMotion() || !animate || (this.#loopActive && !isAdjacentSlide);

    // If jump is more than 1 or we looped, do the placeholder + reorder trick
    if (!instant && !isAdjacentSlide && visibleSlides.length === 1) {
      this.#disabled = true;
      await this.#scroll.finished; // ensure we're not mid-scroll

      const targetSlide = slides[index];
      if (!targetSlide || !currentSlide) return;

      // Create a placeholder in the original DOM position of targetSlide
      const placeholder = document.createElement('slideshow-slide');
      targetSlide.before(placeholder);

      // Decide whether targetSlide goes before or after currentSlide
      // so that we scroll a short distance in the correct direction
      if (requestedIndex < current) {
        currentSlide.before(targetSlide);
      } else {
        currentSlide.after(targetSlide);
      }

      if (current === 0) this.#scroll.to(currentSlide, { instant: true });

      // Once that scroll finishes, restore the DOM
      queueMicrotask(async () => {
        await this.#scroll.finished;
        this.#disabled = false;

        // Restore the slide back to its original position. This triggers a scroll event.
        placeholder.replaceWith(targetSlide);

        // Instantly scroll to the target slide as its position will have changed
        this.#scroll.to(targetSlide, { instant: true });
      });
    }

    const slide = slides[index];
    if (!slide) return;

    const previousIndex = this.current;

    slide.setAttribute('aria-hidden', 'false');

    if (this.#scroll) {
      this.#scroll.to(slide, { instant });
    }

    this.current = this.slides?.indexOf(slide) || 0;

    this.#centerSelectedThumbnail(index, instant ? 'instant' : 'smooth');

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: event != null,
        trigger: 'select',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  }

  /**
   * Advances to the next slide.
   * @param {Event} [event] - The event that triggered the next slide.
   * @param {Object} [options] - The options for the next slide.
   * @param {boolean} [options.animate=true] - Whether to animate the next slide.
   */
  next(event, options) {
    event?.preventDefault();
    this.select(this.nextIndex, event, options);
  }

  /**
   * Goes back to the previous slide.
   * @param {Event} [event] - The event that triggered the previous slide.
   * @param {Object} [options] - The options for the previous slide.
   * @param {boolean} [options.animate=true] - Whether to animate the previous slide.
   */
  previous(event, options) {
    event?.preventDefault();
    this.select(this.previousIndex, event, options);
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (this.#interval) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(value) {
    if (value) {
      this.setAttribute('paused', '');
    } else {
      this.removeAttribute('paused');
    }
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  /**
   * The current slide index.
   * @type {number}
   */
  #current = 0;

  get current() {
    return this.#current;
  }

  /**
   * Sets the current slide index and update the DOM
   * @type {number}
   */
  set current(value) {
    const { current, thumbnails, dots, slides, previous, next } = this.refs;

    this.#current = value;

    if (current) current.textContent = `${value + 1}`;

    for (const controls of [thumbnails, dots]) {
      controls?.forEach((el, i) => el.setAttribute('aria-selected', `${i === value}`));
    }

    if (previous) previous.disabled = Boolean(!this.infinite && value === 0);
    if (next) next.disabled = Boolean(!this.infinite && slides && this.nextIndex >= slides.length);
  }

  get infinite() {
    return this.getAttribute('infinite') != null;
  }

  get visibleSlides() {
    return getVisibleElements(this.refs.scroller, this.slides, SLIDE_VISIBLITY_THRESHOLD, 'x');
  }

  get previousIndex() {
    const { current, visibleSlides } = this;
    const modifier = visibleSlides.length > 1 ? visibleSlides.length : 1;

    return current - modifier;
  }

  get nextIndex() {
    const { current, visibleSlides } = this;
    const modifier = visibleSlides.length > 1 ? visibleSlides.length : 1;

    return current + modifier;
  }

  get atStart() {
    const { current, slides } = this;

    return slides?.length ? current === 0 : false;
  }

  get atEnd() {
    const { current, slides } = this;

    return slides?.length ? current === slides.length - 1 : false;
  }

  /**
   * Sets the disabled attribute.
   * @param {boolean} value - The value to set the disabled attribute to.
   */
  set disabled(value) {
    this.setAttribute('disabled', String(value));
  }
  /**
   * Whether the slideshow is disabled.
   * @type {boolean}
   */
  get disabled() {
    return (
      this.getAttribute('disabled') === 'true' || (this.hasAttribute('mobile-disabled') && !mediaQueryLarge.matches)
    );
  }

  /**
   * Indicates whether the slideshow is temporarily disabled (e.g., during infinite loop transition).
   * @type {boolean}
   */
  #disabled = false;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /**
   * The Scroller instance that manages scrolling.
   * @type {Scroller}
   */
  #scroll;

  /**
   * The ResizeObserver instance for monitoring scroller size changes
   * @type {ResizeObserver}
   */
  #resizeObserver;

  /**
   * Setup the slideshow without controls for zero or one slides
   */
  #setupSlideshowWithoutControls() {
    this.current = 0;
    if (this.hasAttribute('auto-hide-controls')) {
      const { slideshowControls } = this.refs;
      if (slideshowControls instanceof HTMLElement) {
        slideshowControls.hidden = true;
      }
    }

    if (this.refs.slides?.[0]) {
      this.refs.slides[0].setAttribute('aria-hidden', 'false');
    }
  }

  /**
   * Setup the slideshow with controls for when there are multiple slides
   */
  #setupSlideshow() {
    // Setup the scroll instance
    const { scroller } = this.refs;
    this.#scroll = new Scroller(scroller, {
      onScroll: this.#handleScroll,
      onScrollStart: this.#onTransitionInit,
      onScrollEnd: this.#onTransitionEnd,
    });

    scroller.addEventListener('mousedown', this.#handleMouseDown);
    scroller.addEventListener('touchstart', this.#handleTouchStart, { passive: true });
    scroller.addEventListener('touchend', this.#handleTouchEnd, { passive: true });
    scroller.addEventListener('touchcancel', this.#handleTouchEnd, { passive: true });

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('pointerenter', this.#handlePointerEnter);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#updateControlsVisibility();

    this.disabled = this.isNested || this.disabled;

    this.resume();

    this.current = this.initialSlideIndex;

    // Batch reads and writes to the DOM
    scheduler.schedule(() => {
      let visibleSlidesAmount = 0;
      const initialSlideId = this.initialSlide?.getAttribute('slide-id');

      // Wait for next frame to ensure layout is fully calculated before setting initial scroll position
      // This prevents race conditions on Safari mobile when section_width is 'full-width'
      requestAnimationFrame(() => {
        if (this.initialSlideIndex !== 0 && initialSlideId) {
          this.select({ id: initialSlideId }, undefined, { animate: false });
          visibleSlidesAmount = 1;
        } else {
          visibleSlidesAmount = this.#updateVisibleSlides();
          if (visibleSlidesAmount === 0) {
            this.select(0, undefined, { animate: false });
            visibleSlidesAmount = 1;
          }
        }

        // TK: rozjet nekonečný režim ještě před prvním gestem, ať uživatel
        // nikdy nenarazí na kraj.
        this.#recenterLoop();
      });

      this.#resizeObserver = new ResizeObserver(async () => {
        if (viewTransition.current) await viewTransition.current;

        if (visibleSlidesAmount > 1) {
          this.#updateVisibleSlides();
        }

        if (this.hasAttribute('auto-hide-controls')) {
          this.#updateControlsVisibility();
        }

        // TK: počet viditelných snímků se se šířkou mění (carousel vs. grid),
        // takže se nekonečný režim může zapnout i vypnout.
        this.#scheduleRecenter();
      });

      this.#resizeObserver.observe(this.refs.slideshowContainer);
    });
  }

  /**
   * Callback invoked on user initiated scroll to sync the current slide index
   * and emit a slide change event if the index has changed.
   */
  #handleScroll = () => {
    const previousIndex = this.#current;
    const index = this.#sync();

    if (index === previousIndex) return;

    const slide = this.slides?.[index];
    if (!slide) return;

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: true,
        trigger: 'scroll',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  };

  #onTransitionInit = () => {
    this.setAttribute('transitioning', '');
  };

  #onTransitionEnd = () => {
    this.#updateVisibleSlides();
    this.removeAttribute('transitioning');

    // TK: srovnat okolí až po SKUTEČNÉM zastavení, viz #scheduleRecenter().
    this.#scheduleRecenter();
  };

  /**
   * Synchronizes the scroll position and updates the current slide index.
   * @returns {number} The index of the current slide.
   */
  #sync = () => {
    const { slides } = this;
    if (!slides) return (this.current = 0);

    if (!this.#scroll) return (this.current = 0);

    const visibleSlides = this.visibleSlides;

    if (!visibleSlides.length) return this.current;

    const { axis } = this.#scroll;
    const { scroller } = this.refs;
    const centers = visibleSlides.map((slide) => center(slide, axis));
    const referencePoint = visibleSlides.length > 1 ? scroller.getBoundingClientRect()[axis] : center(scroller, axis);
    const closestCenter = closest(centers, referencePoint);
    const closestVisibleSlide = visibleSlides[centers.indexOf(closestCenter)];

    if (!closestVisibleSlide) return (this.current = 0);

    const index = slides.indexOf(closestVisibleSlide);

    return (this.current = index);
  };

  #dragging = false;

  /**
   * TK: nekonečný karusel — DOM index snímku, který je vizuálně první.
   * @type {number}
   */
  #loopOffset = 0;

  /**
   * TK: jestli je nekonečný režim zapnutý. Přepočítává se v #recenterLoop().
   * @type {boolean}
   */
  #loopActive = false;

  /**
   * TK: prst na displeji — v tu chvíli se snímky přeskládat nesmí.
   * @type {boolean}
   */
  #touching = false;

  /**
   * TK: kterým směrem uživatel jede (1 vpřed, -1 zpět). Podle toho se na tu
   * stranu drží rezerva snímků.
   * @type {number}
   */
  #loopDirection = 1;

  /**
   * TK: index při posledním srovnání, kvůli odvození směru.
   * @type {number}
   */
  #loopLastIndex = -1;

  #handleTouchStart = () => {
    // 🔴 Rezervu doplnit HNED, dokud karusel stojí. Při rychlém swipování se
    // totiž nemusí vyskytnout žádná klidová chvilka a snímek by dojel na
    // konec pásu — karusel by se „seknul a nešel dál". Dotek navíc případný
    // dojezd zastavuje, takže tohle je nejspolehlivější klidový okamžik.
    // #recenterLoop() si sám ohlídá, že snímek sedí hranou; když ne, mlčky
    // vyskočí a počká na dojezd.
    this.#recenterLoop();
    this.#touching = true;
  };

  #handleTouchEnd = () => {
    this.#touching = false;
  };

  /**
   * TK: vizuální pozice snímku (0 = úplně vlevo). V nekonečném režimu je
   * vizuální pořadí rotací pořadí v DOM, mimo něj jsou obě totožná.
   * @param {number} index - DOM index snímku.
   * @returns {number}
   */
  #visualPosition(index) {
    const count = this.slides?.length ?? 0;
    if (!this.#loopActive || count === 0) return index;

    return (index - this.#loopOffset + count) % count;
  }

  /**
   * TK: čeká se na plánované srovnání karuselu.
   * @type {boolean}
   */
  #recenterScheduled = false;

  /**
   * TK: 🔴 Odloží srovnání karuselu, dokud se scroll SKUTEČNĚ nezastaví.
   *
   * `Scroller` hlásí konec scrollu 50 ms po posledním scroll eventu. Jenže
   * iOS během setrvačného dojezdu eventy shlukuje a umí mezi nimi udělat
   * delší pauzu, takže „konec scrollu" přijde JEŠTĚ ZA LETU. Když se v tu
   * chvíli přerovnají snímky a opraví `scrollLeft`, prohlížeč si dojezd
   * dokončí z posunuté pozice — a karusel přeletí o několik obrázků.
   * Přesně to hlásil uživatel: „sem tam mi to skočí o 3 obrázky".
   *
   * Proto se poloha hlídá po snímcích a rovná se teprve, když se tři po sobě
   * nehne. Prst na displeji nebo tažení myší čekání ruší — po nich přijde
   * další konec scrollu a naplánuje se to znovu.
   */
  #scheduleRecenter() {
    if (this.#recenterScheduled) return;

    const { scroller } = this.refs;
    if (!scroller) return;

    this.#recenterScheduled = true;

    let previous = scroller.scrollLeft;
    let steady = 0;
    let frames = 0;

    const tick = () => {
      if (!this.isConnected || !this.#scroll) {
        this.#recenterScheduled = false;
        return;
      }

      // Uživatel do toho zase sáhl — tohle čekání zahodit, další konec
      // scrollu naplánuje nové.
      if (this.#touching || this.#dragging) {
        this.#recenterScheduled = false;
        return;
      }

      const position = scroller.scrollLeft;
      steady = Math.abs(position - previous) < 0.5 ? steady + 1 : 0;
      previous = position;

      // ⚠️ Samotný klid NESTAČÍ. Plynulý scroll na konci zpomaluje, takže se
      // pozice pár snímků po sobě mění o desetiny pixelu a vypadá to jako
      // zastaveno — přitom je karusel pořád mezi snímky. Proto musí navíc
      // některý snímek SEDĚT hranou na začátku okna.
      if (steady >= 3 && this.#flushSlide()) {
        this.#recenterScheduled = false;
        this.#recenterLoop();
        return;
      }

      // Pojistka proti věčnému rAF (~4 s). Další konec scrollu to naplánuje znovu.
      if (++frames > 240) {
        this.#recenterScheduled = false;
        return;
      }

      requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);
  }

  /**
   * TK: snímek nejblíž začátku scrollovacího okna, ať sedí hranou nebo ne.
   * Používá se jen na kraji pásu, kde je potřeba doplnit rezervu za každou
   * cenu — viz #recenterLoop().
   * @returns {HTMLElement | null}
   */
  #nearestSlide() {
    const { scroller } = this.refs;
    const slides = this.slides;
    if (!scroller || !slides?.length) return null;

    const left = scroller.getBoundingClientRect().left;
    let best = null;
    let bestDistance = Infinity;

    for (const slide of slides) {
      const distance = Math.abs(slide.getBoundingClientRect().left - left);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = slide;
      }
    }

    return best;
  }

  /**
   * TK: snímek, který sedí hranou na začátku scrollovacího okna, nebo null,
   * když je karusel mezi snímky (dojezd, přichycování).
   * @returns {HTMLElement | null}
   */
  #flushSlide() {
    const { scroller } = this.refs;
    const slides = this.slides;
    if (!scroller || !slides?.length) return null;

    const left = scroller.getBoundingClientRect().left;

    return slides.find((slide) => Math.abs(slide.getBoundingClientRect().left - left) <= 1) ?? null;
  }

  /**
   * TK: srovná šířku snímků na celé FYZICKÉ pixely.
   *
   * Snímek je široký `100%` scrolleru, což je skoro vždycky zlomek (na 1512px
   * okně vyšlo 354,6875 px). Zlomková šířka = zlomkové pozice přichycení,
   * jenže prohlížeč scrollovou pozici kvantizuje na fyzické pixely, takže se
   * netrefí a ze sousedního snímku zůstane po straně viditelný proužek.
   * Naměřeno 0,25 px (Chromium i WebKit, 1512/DPR2) až 0,375 px (1366/DPR1) —
   * přesně to „prosvítá tak 1px na každé straně".
   *
   * ⚠️ Theme na tohle pojistku MÁ, ale je mrtvá: `.product-media-container`
   * si v `base.css` nastaví `--slide-width: round(up, 100%, 1px)` a hned pod
   * tím `width: 100%`, což `slideshow-slide { width: var(--slide-width) }`
   * přebije. A i kdyby ne, procento uvnitř `round()` se nedá vyhodnotit
   * v computed-value čase, takže ho prohlížeč propustí NEZAOKROUHLENÉ.
   * Proto se šířka dorovnává tady a zapisuje se rovnou `width`.
   *
   * Zaokrouhluje se NAHORU: snímek pak scrollovací okno přesahuje, takže se
   * soused nemůže ukázat ani teoreticky. Uřízne to necelý pixel z pravé hrany
   * fotky, což není poznat.
   *
   * @returns {boolean} jestli se šířka změnila
   */
  #alignSlideWidths() {
    const { scroller } = this.refs;
    const slides = this.slides;
    if (!slides?.length) return false;

    const port = scroller.getBoundingClientRect().width;
    if (!(port > 1)) return false;

    const width = slides[0]?.getBoundingClientRect().width ?? 0;
    // Jen karusely „jeden snímek = celá šířka". Režimy s vykukujícím sousedem
    // (hint) mají snímek schválně užší a do těch se nesahá.
    if (Math.abs(width - port) > 1) return false;

    const ratio = window.devicePixelRatio || 1;
    const target = Math.ceil(port * ratio - 0.01) / ratio;
    if (Math.abs(width - target) < 0.01) return false;

    for (const slide of this.refs.slides ?? []) {
      slide.style.setProperty('--slide-width', `${target}px`);
      slide.style.width = `${target}px`;
    }

    return true;
  }

  /**
   * TK: nekonečný karusel. Přerovná snímky tak, aby aktuální stál uprostřed a
   * měl po OBOU stranách kam scrollovat. Swipe přes konec je pak úplně
   * obyčejný nativní swipe na sousední snímek — prohlížeč si ho odjede sám
   * se svou animací i setrvačností a JS do gesta vůbec nesahá.
   *
   * Předchozí verze čekaly, až uživatel narazí na kraj, a skok dopočítávaly
   * v JS až na `touchend`. Na iOS to znamenalo znatelné zpoždění (gesto se
   * nejdřív odrazí od kraje a teprve po zvednutí prstu se něco stane) a
   * chyběla animace posunu. Tudy cesta nevede — kraj nesmí nastat.
   *
   * ⚠️ Pořadí se mění přes flex `order`, NIKDY přesouváním v DOM: `refs` se
   * plní `querySelectorAll` v pořadí DOM (`assets/component.js` → #updateRefs,
   * navíc přes MutationObserver), takže přesun snímku by rozhodil indexy
   * náhledů, teček i `select()`. `order` je čistě vizuální, indexy drží.
   *
   * ⚠️ Přeskládání a oprava `scrollLeft` MUSÍ proběhnout v jedné synchronní
   * dávce — prohlížeč kreslí až po doběhnutí tasku, takže se ven nedostane
   * ani jeden mezisnímek a posun je neviditelný.
   */
  #recenterLoop() {
    const { scroller } = this.refs;
    const slides = this.slides;
    const count = slides?.length ?? 0;
    if (!scroller || !slides) return;

    // 🔴🔴 PRVNÍ ZE VŠEHO: nesahat na nedojetý karusel. Když žádný snímek
    // nesedí hranou na začátku okna, je scroll někde mezi nimi — přeskládat
    // pořadí v tu chvíli znamená, že si dojezd doletí na úplně jiný snímek.
    // Přesně tak vznikal přeskok „sem tam mi to skočí o 3 obrázky".
    // 🔴 Konec pásu = nouzový stav: rezerva došla, další swipe tím směrem
    // nemá kam jet a karusel „nejde dál". Na kraji ale nic neletí (není kam),
    // takže se tam smí doplnit bezpodmínečně — i kdyby zrovna žádný snímek
    // neseděl přesně hranou. Bez téhle pojistky je zaseknutí TRVALÉ: na kraji
    // už scroll nevydá žádnou událost, takže se doplnění nemá čím spustit.
    const maxScroll = scroller.scrollWidth - scroller.clientWidth;
    const atTrackEdge = scroller.scrollLeft <= 0.5 || scroller.scrollLeft >= maxScroll - 0.5;

    const flush = this.#flushSlide() ?? (atTrackEdge ? this.#nearestSlide() : null);
    if (!flush) return;

    // Bezpečnostní srovnání: `current` plní #sync() ze scroll eventů a umí
    // zaostat. Pořadí se počítá z něj, takže zastaralá hodnota = skok.
    const index = slides.indexOf(flush);
    if (index !== -1 && index !== this.current) this.current = index;

    // ⚠️ Aktivita se NESMÍ posuzovat podle `visibleSlides`. Mezi dvěma snímky
    // nemá ani jeden potřebných 70 % viditelnosti, takže vyjde nula, režim se
    // vypne a smaže `order` i šířky — a letící scroll skončí kdekoliv.
    // Rozhoduje proto GEOMETRIE, která na poloze scrollu nezávisí: snímek
    // široký jako okno = karusel „jeden snímek na obrazovku".
    const port = scroller.getBoundingClientRect().width;
    const slideWidth = flush.getBoundingClientRect().width;
    const active =
      this.infinite &&
      !this.disabled &&
      count >= 3 &&
      this.#scroll?.axis === 'x' &&
      port > 1 &&
      Math.abs(slideWidth - port) <= 1.5;

    if (!active) {
      if (this.#loopActive) {
        for (const slide of this.refs.slides ?? []) {
          slide.style.removeProperty('order');
          slide.style.removeProperty('width');
          slide.style.removeProperty('--slide-width');
          slide.style.removeProperty('scroll-snap-stop');
        }
        this.#loopActive = false;
        this.#loopOffset = 0;
        this.#loopLastIndex = -1;
      }
      return;
    }

    // Nejdřív šířky — přerovnání i oprava scrollu níž musí počítat s finální
    // geometrií.
    const resized = this.#alignSlideWidths();

    // 🔴 Rezerva se drží VE SMĚRU JÍZDY, ne symetricky kolem aktuálního
    // snímku. Symetrické centrování dávalo dopředu jen půlku snímků (u devíti
    // čtyři), jenže doplnění rezervy smí proběhnout jen když karusel stojí
    // zarovnaný — a při rychlém swipování sahá uživatel na displej ještě za
    // letu, takže se doplnění pokaždé přeskočí. Po pár rychlých swipech se
    // proto dojelo na konec pásu a karusel „nešel dál", dokud se uživatel na
    // chvíli nezastavil. Směrová rezerva dá dopředu `count - 1 - lead`
    // snímků (u devíti šest), což žádná série swipů nevyčerpá.
    const lead = Math.min(2, Math.floor((count - 1) / 2));
    const position = this.#visualPosition(this.current);

    // Odvození směru z posledního srovnání, kratší cestou po kruhu.
    if (this.#loopLastIndex !== -1 && this.current !== this.#loopLastIndex) {
      const forward = (this.current - this.#loopLastIndex + count) % count;
      this.#loopDirection = forward <= count - forward ? 1 : -1;
    }
    this.#loopLastIndex = this.current;

    // Kolik snímků zbývá tím směrem, kterým uživatel jede.
    const runway = this.#loopDirection > 0 ? count - 1 - position : position;

    // Doplňuje se, dokud vepředu ještě dva snímky jsou. Čekat až na jeden je
    // moc těsné: doplnění smí proběhnout jen když karusel stojí zarovnaný, a
    // když se to jednou nepotká, byl by konec pásu.
    if (this.#loopActive && runway >= 3 && !atTrackEdge) {
      // Pořadí sedí, ale posunuté hrany je potřeba dohnat scrollem.
      if (resized) this.#scroll?.to(slides[this.current], { instant: true });
      return;
    }

    const target = this.#loopDirection > 0 ? lead : count - 1 - lead;
    const offset = (this.current - target + count) % count;

    this.#loopActive = true;
    this.#loopOffset = offset;

    for (const slide of this.refs.slides ?? []) {
      const slideIndex = slides.indexOf(slide);

      if (slideIndex === -1) slide.style.removeProperty('order');
      else slide.style.order = `${(slideIndex - offset + count) % count}`;

      // 🔴 Jeden swipe = jeden snímek. Bez tohohle prohlížeč při prudkém
      // gestu přeletí několik bodů přichycení naráz — a kromě toho, že to
      // uživatel nechce, se tím vyčerpá rezerva po straně dřív, než se stihne
      // doplnit, a karusel dojede na konec pásu.
      slide.style.scrollSnapStop = 'always';
    }

    // Aktuální snímek se přesunul jinam, scroll ho musí dohnat. Instantně a
    // hned teď, ať se rozdíl nikdy nevykreslí.
    const currentSlide = slides[this.current];
    if (currentSlide) this.#scroll?.to(currentSlide, { instant: true });
  }

  /**
   * Handles the 'mousedown' event to start dragging slides.
   * @param {MouseEvent} event - The mousedown event.
   */
  #handleMouseDown = (event) => {
    const { slides } = this;

    if (!slides || slides.length <= 1) return;
    if (!(event.target instanceof Element)) return;
    if (this.disabled || this.#dragging) return;

    // Check if the event target is within a 3D model interactive element
    // This prevents the slideshow from capturing drag events when interacting with 3D models
    if (event.target.closest('model-viewer')) {
      return;
    }

    event.preventDefault();
    // Doplnit rezervu, dokud karusel stojí — viz #handleTouchStart.
    this.#recenterLoop();

    // Store initial position but don't start handling yet
    const { axis } = this.#scroll;
    const startPosition = event[axis];

    const controller = new AbortController();
    const { signal } = controller;
    const startTime = performance.now();
    let previous = startPosition;
    let velocity = 0;
    let moved = false;
    let distanceTravelled = 0;
    // TK: track recent pointer samples (last ~100ms) for accurate end velocity.
    // Original Tinker code used delta/(now-startTime) which under-reports for
    // long slow holds (slow drag for 2s → velocity ≈ 0 even on prudké release).
    /** @type {Array<{t:number, p:number}>} */
    const samples = [{ t: startTime, p: startPosition }];

    this.#dragging = true;

    /**
     * Handles the 'pointermove' event to update the scroll position.
     * @param {PointerEvent} event - The pointermove event.
     */
    const onPointerMove = (event) => {
      const current = event[axis];
      const initialDelta = startPosition - current;

      if (!initialDelta) return;

      if (!moved) {
        moved = true;
        this.setPointerCapture(event.pointerId);

        // Prevent clicks once the user starts dragging
        document.addEventListener('click', preventDefault, { once: true, signal });

        const movingRight = initialDelta < 0;
        const movingLeft = initialDelta > 0;

        // Check if the current slideshow should handle this drag
        const closestSlideshow = this.parentElement?.closest('slideshow-component');
        const isNested = closestSlideshow instanceof Slideshow && closestSlideshow !== this;
        const cannotMoveInDirection = (movingRight && this.atStart) || (movingLeft && this.atEnd);

        // Abort and let the parent slideshow handle the drag if we're moving in a direction where nested slideshow can't move
        if (isNested && cannotMoveInDirection) {
          controller.abort();
          return;
        }

        this.pause();
        this.setAttribute('dragging', '');
      }

      // Stop the event from bubbling up to parent slideshow components
      event.stopImmediatePropagation();

      const delta = previous - current;
      const now = performance.now();
      // Window-based velocity (last 100 ms of motion). Direction sign matches
      // scroll axis: positive = scroll toward next slide.
      samples.push({ t: now, p: current });
      while (samples.length > 2 && now - samples[0].t > 100) samples.shift();
      if (samples.length >= 2) {
        const first = samples[0];
        const last = samples[samples.length - 1];
        const dt = last.t - first.t;
        velocity = dt > 0 ? Math.round(((first.p - last.p) / dt) * 1000) : 0;
      }
      previous = current;
      distanceTravelled += Math.abs(delta);

      this.#scroll.by(delta, { instant: true });
    };

    /**
     * Handles the 'pointerup' event to stop dragging slides.
     * @param {PointerEvent} event - The pointerup event.
     */
    const onPointerUp = async (event) => {
      controller.abort();
      const { current, slides } = this;
      const { scroller } = this.refs;

      this.#dragging = false;

      if (!slides?.length || !scroller) return;

      // TK: derive overall drag direction (used as fallback when velocity has
      // settled to ~0 right before release — common for active hold-drag).
      const overallDelta = startPosition - previous; // >0 = toward next slide
      const direction = Math.sign(velocity) || Math.sign(overallDelta);
      const next = this.#sync();

      // TK: distance-ratio threshold. If user dragged ≥18% of slide extent in
      // the drag axis, advance regardless of release velocity. Below threshold
      // (or no direction at all) → snap back. Original Tinker logic required
      // velocity ≥ 10 OR a 50% drag (via #sync crossing center) → too sticky
      // for slow active holds.
      const slideEl = slides[current];
      const slideRect = slideEl ? slideEl.getBoundingClientRect() : null;
      const slideExtent = slideRect ? (axis === 'x' ? slideRect.width : slideRect.height) : 0;
      const dragRatio = slideExtent > 0 ? distanceTravelled / slideExtent : 0;

      let modifier;
      if (current !== next) {
        // #sync already crossed center → next is the target slide
        modifier = 0;
      } else if (direction !== 0 && (dragRatio >= 0.18 || Math.abs(velocity) >= 10) && distanceTravelled >= 10) {
        modifier = direction;
      } else {
        modifier = 0;
      }
      const rawIndex = next + modifier;

      // TK: v nekonečném režimu je za posledním snímkem zase první, takže se
      // index otáčí modulem místo doražení na kraj.
      const newIndex = this.#loopActive
        ? ((rawIndex % slides.length) + slides.length) % slides.length
        : clamp(rawIndex, 0, slides.length - 1);

      const newSlide = slides[newIndex];
      const currentIndex = this.current;

      if (!newSlide) throw new Error(`Slide not found at index ${newIndex}`);

      this.#scroll.to(newSlide);

      this.removeAttribute('dragging');
      this.releasePointerCapture(event.pointerId);

      this.#centerSelectedThumbnail(newIndex);

      this.dispatchEvent(
        new SlideshowSelectEvent({
          index: newIndex,
          previousIndex: currentIndex,
          userInitiated: true,
          trigger: 'drag',
          slide: newSlide,
          id: newSlide.getAttribute('slide-id'),
        })
      );

      this.current = newIndex;

      await this.#scroll.finished;

      // It's possible that the user started dragging again before the scroll finished
      if (this.#dragging) return;

      this.#scroll.snap = true;
      this.resume();
    };

    this.#scroll.snap = false;

    document.addEventListener('pointermove', onPointerMove, { signal });
    document.addEventListener('pointerup', onPointerUp, { signal });
    /**
     * pointerDown calls onPointerUp to fix an issue where the first tap-and-drag
     * on the zoom dialog is captured by the pointerMove/pointerUp listeners,
     * sometimes causing the slideshow to change slides unexpectedly
     */
    document.addEventListener('pointerdown', onPointerUp, { signal });
    document.addEventListener('pointercancel', onPointerUp, { signal });
    document.addEventListener('pointercapturelost', onPointerUp, { signal });
  };

  #handlePointerEnter = () => {
    this.setAttribute('actioned', '');
  };

  get slides() {
    return this.refs.slides?.filter((slide) => !slide.hasAttribute('hidden') || slide.hasAttribute('reveal'));
  }

  /**
   * The initial slide index.
   * @type {number}
   */
  get initialSlideIndex() {
    const initialSlide = this.getAttribute('initial-slide');
    if (initialSlide == null) return 0;

    return parseInt(initialSlide, 10);
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());

  #updateControlsVisibility() {
    if (!this.hasAttribute('auto-hide-controls')) return;

    const { scroller, slideshowControls } = this.refs;

    if (!(slideshowControls instanceof HTMLElement)) return;

    slideshowControls.hidden = scroller.scrollWidth <= scroller.offsetWidth;
  }

  /**
   * Centers the selected thumbnail in the thumbnails container
   * @param {number} index - The index of the selected thumbnail
   * @param {ScrollBehavior} [behavior] - The scroll behavior.
   */
  #centerSelectedThumbnail(index, behavior = 'smooth') {
    const selectedThumbnail = this.refs.thumbnails?.[index];
    if (!selectedThumbnail) return;

    const { thumbnailsContainer } = this.refs;
    if (!thumbnailsContainer || !(thumbnailsContainer instanceof HTMLElement)) return;

    const { slideshowControls } = this.refs;
    if (!slideshowControls || !(slideshowControls instanceof HTMLElement)) return;

    scrollIntoView(selectedThumbnail, {
      ancestor: thumbnailsContainer,
      behavior,
      block: 'center',
      inline: 'center',
    });
  }

  #updateVisibleSlides() {
    const { slides } = this;
    if (!slides || !slides.length) return 0;

    const visibleSlides = this.visibleSlides;

    // Batch writes to the DOM
    scheduler.schedule(() => {
      // Update aria-hidden based on visibility
      slides.forEach((slide) => {
        const isVisible = visibleSlides.includes(slide);
        slide.setAttribute('aria-hidden', `${!isVisible}`);
      });
    });

    return visibleSlides.length;
  }
}

if (!customElements.get('slideshow-component')) {
  customElements.define('slideshow-component', Slideshow);
}
